<template>
	<form class="registration-form" @submit.prevent="submitForm">
		<errors-list :errors="errors"/>
		<input type="hidden" :name="fields.facebook_token" v-model="facebook_token" v-if="facebook_token">
		<input type="hidden" :name="fields.google_token" v-model="google_token" v-if="google_token">
		<input type="hidden" :name="fields.apple_token" v-model="apple_token" v-if="apple_token">
		<input type="hidden" :name="fields.invite_hash" v-model="inviteHash" v-if="inviteHash">
		<input type="hidden" :name="fields.username">
		<div class="form-row two-columns" v-if="!apple_token">
			<default-field-box type="text" :name="fields.first_name" :placeholder="$t('forms.first_name')"
			                   :value="first_name"
			                   :required="true"/>
			<default-field-box type="text" :name="fields.last_name" :placeholder="$t('forms.last_name')"
			                   :value="last_name"
			                   :required="true"/>
		</div>
		<div class="form-row two-columns">
			<default-field-box type="email" :name="fields.email" :placeholder="$t('forms.email')" :value="email"
			                   :required="true" :readonly="readOnlyEmail" :disable-copy-paste="true"/>
			<default-field-box type="email" :name="fields.email_confirmation"
			                   :placeholder="$t('forms.email_confirmation')"
			                   :value="email_confirmation"
			                   :required="true" :readonly="readOnlyEmail" :disable-copy-paste="true"/>
		</div>
		<div class="form-row two-columns" v-if="showPasswordFields">
			<default-field-box type="password" :name="fields.password" :placeholder="$t('forms.password')"
			                   :required="true"
			                   :password-validation="true"/>
			<default-field-box type="password" :name="fields.password_confirmation"
			                   :placeholder="$t('forms.password_confirmation')" :required="true"/>
		</div>

		<template v-if="alliances.length > 1">
			<div class="form-row">
				<default-field-box type="tel" :name="fields.phone" :placeholder="$t('forms.phone')" :value="phone"
				                   :required="true"/>
			</div>
			<template v-for="(item, index) in agents">
				<div class="form-row two-columns" :key="item.id">
					<default-field-box type="text" :name="`${fields.agent_code}[${index}]`"
					                   :placeholder="$t('forms.agent_code')" :value="agent_code"/>
					<default-field-box type="select" :name="`${fields.agent_alliance}[${index}]`"
					                   :placeholder="$t('forms.agent_alliance')" :options="allianceOptions"/>
				</div>
				<div class="form-row">
					<button class="button style-6 full-width small" @click.prevent="removeAgent(item.id)">
						<span class="inner">{{ $t('forms.remove_agent') }}</span>
					</button>
				</div>
			</template>
			<div class="form-row">
				<button class="button style-3 full-width small" @click.prevent="addAgent">
					<span class="inner">{{ $t('forms.add_agent') }}</span>
				</button>
			</div>
		</template>
		<template v-else>
			<div class="form-row two-columns">
				<default-field-box type="tel" :name="fields.phone" :placeholder="$t('forms.phone')" :value="phone"
				                   :required="true"/>
				<default-field-box type="text" :name="fields.agent_code" :placeholder="$t('forms.agent_code')"
				                   :value="agent_code"/>
			</div>
			<input type="hidden" :name="fields.agent_alliance" :value="alliances[0].code">
		</template>

		<div class="form-row">
			<label class="checkbox-box">
				<input type="checkbox" :name="fields.privacy_policy" value="1">
				<span class="status"></span>
				<span class="field-title"
				      v-html="$t('forms.agreement_privacy_policy', {link: domainUrl + $t('routes.privacy_policy_finance.path')})"></span>
			</label>
		</div>
		<div class="form-row">
			<label class="checkbox-box">
				<input type="checkbox" :name="fields.terms" value="1">
				<span class="status"></span>
				<span class="field-title"
				      v-html="$t('forms.agreement_terms', {link: domainUrl + $t('routes.terms.path')})"></span>
			</label>
		</div>
		<div class="form-row">
			<label class="checkbox-box">
				<input type="checkbox" :name="fields.marketing" value="1">
				<span class="status"></span>
				<span class="field-title"
				      v-html="$t('forms.agreement_marketing', {link: domainUrl + $t('routes.privacy_policy_marketing.path')})"></span>
			</label>
		</div>
		<div class="form-row">
			<label class="checkbox-box">
				<input type="checkbox" :name="fields.financial_services" value="1">
				<span class="status"></span>
				<span class="field-title"
				      v-html="$t('forms.agreement_financial_services', {link: domainUrl + $t('routes.financial_services.path')})"></span>
			</label>
		</div>
		<div class="form-row">
			<p class="recaptcha-disclaimer"
			   v-html="$t('forms.recaptcha_disclaimer', {
			     privacy_policy_link: $t('forms.recaptcha_privacy_policy_link'),
			     terms_of_service_link: $t('forms.recaptcha_terms_of_service_link')
			   })">
			</p>
		</div>
		<div class="form-actions">
			<button type="submit" class="button style-4" :class="loading ? 'loading' : ''">
				<span class="inner">{{ $t('auth.create_account') }}</span>
			</button>
		</div>
	</form>
</template>

<script>
import ErrorsList from "../containers/ErrorsList";
import DefaultFieldBox from "../form-fields/DefaultFieldBox";
import {Device} from "@capacitor/device";
import { trackEvent } from '@/analytics';

export default {
	name: "RegistrationForm",
	components: {
		ErrorsList,
		DefaultFieldBox,
	},
	data() {
		return {
			agentIndex: 1,
			errors: false,
			loading: false,
			email: '',
			email_confirmation: '',
			first_name: '',
			last_name: '',
			agent_code: '',
			agent_alliance: '',
			phone: '',
			password: '',
			password_confirmation: '',
			facebook_token: '',
			google_token: '',
			apple_token: '',
			readOnlyEmail: false,
			inviteHash: false,
			agents: [],
			showPasswordFields: true,
		}
	},
	computed: {
		domainUrl() {
			return process.env.VUE_APP_URL;
		},
		fields() {
			return this.$store.state.popups.registrationFields;
		},
		alliances() {
			return this.$store.state.popups.alliances;
		},
		allianceOptions() {
			let options = [];
			this.alliances.forEach(item => {
				options.push({
					title: item.name,
					value: item.code,
				});
			});

			return options;
		}
	},
	methods: {
		addAgent() {
			//todo checknut tento test
			this.agents.push({
				id: this.agentIndex,
				name: `test ${this.agentIndex}`
			});
			this.agentIndex++;
		},
		removeAgent(id) {
			this.agents = this.agents.filter(item => item.id !== id);
		},
		async submitForm(e) {
			this.loading = true;
			trackEvent('form_submit', { form_name: 'registration', module: 'auth' });

			let form = e.target;
			let formData = new FormData(form);

			if (this.$store.state.app.native) {
				const deviceId = await Device.getId();
				formData.append('device_id', deviceId.identifier);
			}

			await this.$recaptchaLoaded()
			const token = await this.$recaptcha('register');
			formData.append('recaptcha_token', token);

			let afterLoginAction = this.$store.state.auth.afterLoginAction;
			if (afterLoginAction) {
				formData.append('after_login_action', afterLoginAction);
			}

			this.axios.post('/api/register', formData, {
				headers: {
					'X-Session-Id': this.$store.state.app.sessionId,
				}
			}).then(response => {
				this._submitSuccess = true;
				trackEvent('form_submit_success', { form_name: 'registration', module: 'auth' });
				this.$store.commit('SET_INVITE_HASH', false);
				this.$store.commit('SET_INTRANET_USER', false);

				this.loading = false;
				this.$store.dispatch('closePopups');

				let text = '';
				if (response.data.verification) {
					text = this.$t('auth.registration_successful_text');
				}

				this.$swal.fire({
					icon: 'success',
					title: this.$t('auth.registration_successful_title'),
					text: text,
				});
			}).catch(error => {
				trackEvent('form_submit_error', {
					form_name: 'registration',
					module: 'auth',
					error_message: error.response && error.response.data ? error.response.data.message : error.message,
					error_status: error.response ? String(error.response.status) : null,
				});
				this.$swal.fire({
					icon: 'error',
					title: 'Oops',
					text: error.response.data.message
				}).then(() => {
					setTimeout(() => {
						this.$root.$emit('scrollToTop');
					}, 300)
				});

				if (error.response.data.errors) {
					this.errors = error.response.data.errors;
				}
				this.loading = false;
			});
		},
		preFillIntranetUser() {
			let data = this.$store.state.auth.intranetUser;
			if (!data) {
				return false;
			}

			this.first_name = data.first_name;
			this.last_name = data.last_name;
			this.email = data.email;
			this.email_confirmation = data.email;
			this.phone = data.phone.mobile1;
			this.agent_code = data.acquired_agent.id;
			this.inviteHash = this.$store.state.auth.inviteHash;
		},
		preFillAgent() {
			let agent = this.$store.state.auth.inviteAgent;
			if (!agent) {
				return false;
			}

			this.agent_code = agent;
		}
	},
	mounted() {
		this._submitSuccess = false;
		trackEvent('form_open', { form_name: 'registration', module: 'auth' });
		this.$root.$on('socials-data-received', (data) => {
			this.first_name = data.first_name;
			this.last_name = data.last_name;
			this.email = data.email;
			this.email_confirmation = data.email;
			this.readOnlyEmail = true;
			this.showPasswordFields = false;

			if (data.facebook_token) {
				this.facebook_token = data.facebook_token;
			}
			if (data.google_token) {
				this.google_token = data.google_token;
			}
			if (data.apple_token) {
				this.apple_token = data.apple_token;
			}
		});

		this.preFillIntranetUser();
		this.preFillAgent();
	},
	beforeDestroy() {
		if (!this._submitSuccess) {
			trackEvent('form_close', { form_name: 'registration', module: 'auth' });
		}
	},
	destroyed() {
		this.$root.$off('socials-data-received');
	}
}
</script>

<style lang="scss">
.full-width {
	width: 100%;
}

.recaptcha-disclaimer {
	font-size: 12px;

	a {
		color: $whiteColor;
		text-decoration: underline;

		&:hover {
			color: $primaryColor;
		}
	}
}
</style>